import { WizardState } from './campaign-wizard';
import { campaignCurrencies } from './upsell-step';
import { IcoCheck, IcoX } from '@components/icons';
import { useCurrentTenant } from 'client/lib/auth';
import { useEffect, useRef, useState } from 'preact/hooks';

type Props = {
  state: WizardState;
};

type ValidationItem = {
  label: string;
  pass: boolean;
  detail?: string;
};

function getValidation(state: WizardState): ValidationItem[] {
  const items: ValidationItem[] = [];

  items.push({
    label: 'Course selected',
    pass: !!state.courseId,
    detail: state.courseTitle || 'No course',
  });

  items.push({
    label: 'URL slug set',
    pass: !!state.slug,
    detail: state.slug ? `/c/${state.slug}` : 'Missing',
  });

  items.push({
    label: 'Price selected',
    pass: !!state.selectedPriceId,
    detail: state.selectedPriceId || 'No price',
  });

  const hasHero = state.sections.some((s) => s.type === 'hero');
  items.push({
    label: 'Hero section configured',
    pass: hasHero,
  });

  const heroSection = state.sections.find((s) => s.type === 'hero');
  if (heroSection && heroSection.type === 'hero') {
    items.push({
      label: 'Hero headline set',
      pass: !!heroSection.content.headline,
    });
  }

  const hasCheckout = state.sections.some(
    (s) => s.type === 'integratedCheckout' || s.type === 'checkoutForm',
  );
  items.push({
    label: 'Checkout form included',
    pass: hasCheckout,
  });

  if (state.upsellEnabled) {
    const isSelfUpsell = state.upsellProductCourseId === state.courseId;
    items.push({
      label: 'Upsell product selected',
      pass: !!state.upsellProductCourseId && !isSelfUpsell,
      detail: isSelfUpsell
        ? 'Cannot upsell to the same course'
        : state.upsellProductTitle || 'Not set',
    });

    // One per checkout currency: a CAD buyer is only offered a CAD price.
    const currencies = campaignCurrencies(state);
    for (const currency of currencies) {
      const priceId = state.upsellPriceIds[currency];
      items.push({
        label:
          currencies.length > 1 ? `Upsell price for ${currency} buyers` : 'Upsell price selected',
        pass: !!priceId,
        detail:
          priceId ||
          (currencies.length > 1 ? `Not set — ${currency} buyers won't see the upsell` : 'Not set'),
      });
    }

    const hasCta = state.upsellSections.some((s) => s.type === 'cta');
    items.push({
      label: 'Upsell CTA section present',
      pass: hasCta,
    });
  }

  if (state.welcomeEnabled) {
    items.push({
      label: 'Thank-you headline set',
      pass: !!state.welcomeHeadline,
    });
  }

  if (state.redirectEnabled) {
    items.push({
      label: 'Redirect URL set',
      pass: !!state.redirectUrl && /^https?:\/\//.test(state.redirectUrl),
      detail: state.redirectUrl || 'Not set',
    });
    items.push({
      label: 'Redirect date set',
      pass: !!state.redirectAt,
      detail: state.redirectAt ? new Date(state.redirectAt).toLocaleString() : 'Not set',
    });
  }

  return items;
}

export function ReviewStep({ state }: Props) {
  const validation = getValidation(state);
  const allPassed = validation.every((v) => v.pass);

  return (
    <div>
      <h2 class="text-lg font-semibold text-gray-900 mb-1">Review & Publish</h2>
      <p class="text-sm text-gray-500 mb-5">Verify your campaign is ready before publishing.</p>

      <div class="grid gap-6 lg:grid-cols-2">
        <div>
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Checklist</h3>
          <div class="flex flex-col gap-2">
            {validation.map((item, i) => (
              <div
                key={i}
                class={`flex items-center gap-3 p-3 rounded-lg ${
                  item.pass ? 'bg-green-50' : 'bg-red-50'
                }`}
              >
                {item.pass ? (
                  <IcoCheck class="size-5 text-green-600 flex-shrink-0" />
                ) : (
                  <IcoX class="size-5 text-red-500 flex-shrink-0" />
                )}
                <div class="flex-1">
                  <span class="text-sm font-medium text-gray-900">{item.label}</span>
                  {item.detail && <span class="text-xs text-gray-500 ml-2">{item.detail}</span>}
                </div>
              </div>
            ))}
          </div>

          {!allPassed && (
            <p class="mt-4 text-sm text-amber-600">
              Some checks didn't pass. You can still save as draft.
            </p>
          )}
        </div>

        <div>
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Summary</h3>
          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-3 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Course</span>
              <span class="font-medium text-gray-900">{state.courseTitle || 'Not set'}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">URL</span>
              <span class="font-medium text-gray-900">
                {state.slug ? `/c/${state.slug}` : 'Not set'}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Sections</span>
              <span class="font-medium text-gray-900">{state.sections.length}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Status</span>
              <span
                class={`font-medium ${state.useCustomCheckout ? 'text-green-600' : 'text-gray-500'}`}
              >
                {state.useCustomCheckout ? 'Published' : 'Draft'}
              </span>
            </div>
            {state.freeTrialDays && (
              <div class="flex justify-between">
                <span class="text-gray-500">Free trial</span>
                <span class="font-medium text-gray-900">{state.freeTrialDays} days</span>
              </div>
            )}
            {state.deferredDate && (
              <div class="flex justify-between">
                <span class="text-gray-500">Enrollment date</span>
                <span class="font-medium text-gray-900">
                  {new Date(state.deferredDate + 'T00:00:00').toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            )}
            <div class="flex justify-between">
              <span class="text-gray-500">Thank-you page</span>
              <span class="font-medium text-gray-900">
                {state.welcomeEnabled ? 'Custom' : 'Default'}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Upsell</span>
              <span class="font-medium text-gray-900">
                {state.upsellEnabled ? state.upsellProductTitle || 'Enabled' : 'None'}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">End redirect</span>
              <span class="font-medium text-gray-900">
                {state.redirectEnabled
                  ? state.redirectAt
                    ? new Date(state.redirectAt).toLocaleString()
                    : 'Enabled (no date)'
                  : 'None'}
              </span>
            </div>
          </div>

          {state.slug && <CampaignUrls state={state} />}
        </div>
      </div>
    </div>
  );
}

function CopyableUrl(props: { label: string; url: string; href?: string; note?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  const copy = () => {
    navigator.clipboard
      .writeText(props.url)
      .then(() => {
        setCopied(true);
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <div class="flex flex-col gap-0.5">
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs font-semibold text-gray-700">{props.label}</span>
        <button
          type="button"
          onClick={copy}
          class="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex-shrink-0"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {props.href ? (
        <a
          href={props.href}
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-gray-600 underline break-all"
        >
          {props.url}
        </a>
      ) : (
        <span class="text-xs text-gray-600 break-all">{props.url}</span>
      )}
      {props.note && <span class="text-[11px] text-gray-400">{props.note}</span>}
    </div>
  );
}

function CampaignUrls({ state }: { state: WizardState }) {
  const tenant = useCurrentTenant();
  const base = `https://${tenant.domain}`;
  const hasCourse = !!state.courseId && state.courseId !== 'new';

  return (
    <div class="mt-4 p-5 bg-gray-50 rounded-xl flex flex-col gap-4">
      <div class="flex items-center justify-between gap-2">
        <p class="text-sm font-semibold text-gray-800">Campaign URLs</p>
        <span
          class={`text-xs font-medium ${
            state.useCustomCheckout ? 'text-green-600' : 'text-amber-600'
          }`}
        >
          {state.useCustomCheckout ? 'Published' : 'Draft — publish to go live'}
        </span>
      </div>

      <CopyableUrl
        label="Landing page"
        url={`${base}/c/${state.slug}`}
        href={`${base}/c/${state.slug}`}
        note="Public page — point ads and links here."
      />

      {hasCourse && (
        <CopyableUrl
          label="Thank-you page"
          url={`${base}/courses/${state.courseId}/provision`}
          note="Loads only after a completed purchase — not a public link."
        />
      )}

      {hasCourse && state.upsellEnabled && state.selectedPriceId && (
        <CopyableUrl
          label="Upsell page"
          url={`${base}/upsell?priceId=${state.selectedPriceId}`}
          note="Post-purchase step, reached mid-checkout only."
        />
      )}
    </div>
  );
}
